import { Brain, Clock3, FileDown, Gauge, Layers3, ShieldCheck, Sparkles, Timer, Zap } from "lucide-react";
import type { ReactNode } from "react";
import type { PremiumReport, ReportDimensionBreakdown } from "../../../shared/types";
import { Button } from "../ui/Button";

function formatDuration(ms: number) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return minutes > 0 ? `${minutes}λ ${remaining}δ` : `${remaining}δ`;
}

function ageBandLabel(ageBand?: PremiumReport["ageBand"]) {
  if (!ageBand) return "Δεν δηλώθηκε";
  return ageBand === "55-plus" ? "55+" : ageBand.replace("-", "–");
}

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, value));
}

function iconForDimension(dimension: ReportDimensionBreakdown) {
  const className = "text-indigo-600";
  if (dimension.icon === "cube") return <Layers3 className={className} size={28} />;
  if (dimension.icon === "sigma") return <Brain className={className} size={28} />;
  if (dimension.icon === "logic") return <ShieldCheck className={className} size={30} />;
  if (dimension.icon === "timer") return <Timer className={className} size={28} />;
  return <Sparkles className={className} size={28} />;
}

function performanceLabel(report: PremiumReport) {
  if (report.score.brainTestScore >= 128) return "Εξαιρετική επίδοση";
  if (report.score.brainTestScore >= 115) return "Ισχυρή επίδοση";
  if (report.score.brainTestScore >= 100) return "Σταθερή επίδοση";
  return "Αναπτυσσόμενο προφίλ";
}

function compactInterpretation(text: string) {
  const sentences = text
    .split(". ")
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  const firstTwo = sentences.slice(0, 2).join(". ");
  return firstTwo.endsWith(".") ? firstTwo : `${firstTwo}.`;
}

function neuterTimePerformanceLabel(label: PremiumReport["timing"]["timePerformanceLabel"]) {
  const labels: Record<PremiumReport["timing"]["timePerformanceLabel"], string> = {
    Γρήγορη: "γρήγορο",
    Σταθερή: "σταθερό",
    Προσεκτική: "προσεκτικό",
    "Αργή αλλά σταθερή": "αργό αλλά σταθερό"
  };
  return labels[label] ?? label.toLowerCase();
}

function percentileSentence(label: string) {
  if (label.startsWith("Η επίδοσή σου")) return label;
  const percent = label.match(/\d+/)?.[0];
  return percent
    ? `Η επίδοσή σου είναι υψηλότερη από περίπου το ${percent}% των χρηστών.`
    : `Η επίδοσή σου είναι ${label}.`;
}

function consistencyIndexLabel(label: PremiumReport["responseQuality"]["consistencyLabel"]) {
  const labels: Partial<Record<PremiumReport["responseQuality"]["consistencyLabel"], string>> = {
    Υψηλή: "Υψηλός",
    Καλή: "Καλός",
    Μέτρια: "Μέτριος",
    Χαμηλή: "Χαμηλός"
  };
  return labels[label] ?? label;
}

function naturalDimensionInterpretation(text: string) {
  return text
    .replace(/Ισχυρή και σταθερή επίδοση στη διάσταση /g, "Ισχυρή και σταθερή επίδοση στο πεδίο ")
    .replace(/Καλή επίδοση με καθαρή κατανόηση των βασικών κανόνων στη διάσταση /g, "Καλή επίδοση με καθαρή κατανόηση των βασικών κανόνων στο πεδίο ")
    .replace(/Μέτρια επίδοση με περιθώριο βελτίωσης στη διάσταση /g, "Μέτρια επίδοση με περιθώριο βελτίωσης στο πεδίο ")
    .replace(/Η διάσταση (.+?) φαίνεται να χρειάζεται πιο συστηματική εξάσκηση\./g, "Στο πεδίο $1 φαίνεται ότι χρειάζεται πιο συστηματική εξάσκηση.");
}

function interpretationCards(report: PremiumReport) {
  const rushedCopy =
    report.responseQuality.rushedGuessCount > 0
      ? `Υπάρχουν ${report.responseQuality.rushedGuessCount} πολύ γρήγορες λάθος απαντήσεις. Ένας σύντομος δεύτερος έλεγχος στα πιο απαιτητικά items μπορεί να ανεβάσει την ακρίβεια χωρίς να χαθεί ο ρυθμός.`
      : "Δεν εμφανίζεται έντονη ένδειξη βιαστικών λαθών. Κράτησε την ίδια προσεκτική στρατηγική στα πιο απαιτητικά items.";

  return [
    {
      title: "Συνολική εικόνα",
      body: compactInterpretation(report.interpretation)
    },
    {
      title: "Δυνατό σημείο",
      body: report.strengths.paragraph
    },
    {
      title: "Ρυθμός απάντησης",
      body: `Το χρονικό προφίλ σου χαρακτηρίζεται ως ${neuterTimePerformanceLabel(report.timing.timePerformanceLabel)}, με μέσο χρόνο ${formatDuration(report.averageTimeMs)} ανά ερώτηση.`
    },
    {
      title: "Τι να προσέξεις",
      body: rushedCopy
    }
  ];
}

function PrintStyle({ mode }: { mode: "screen" | "pdf" }) {
  return (
    <style>{`
      ${mode === "pdf" ? `
        .report-mode-pdf {
          padding: 32px 32px 8px !important;
        }
        .report-mode-pdf .report-canvas {
          width: 100%;
          max-width: 1152px;
          overflow: hidden;
          isolation: isolate;
        }
        .report-mode-pdf .report-canvas > section:first-child {
          border-top-left-radius: 1rem;
          border-top-right-radius: 1rem;
        }
        .report-mode-pdf .pdf-export-spacer {
          visibility: hidden;
          pointer-events: none;
        }
        /*
          PDF LAYOUT GUARD:
          The "Ανάλυση δεξιοτήτων" section is intentionally compacted in PDF mode
          so all five skill cards fit on one page across variable Greek report text.
          Do not change .pdf-skills-* / .pdf-skill-* spacing, padding, line-height,
          or break rules without comparing against reference PDFs CB3A0 and AEED8.
        */
        .report-mode-pdf .pdf-skills-panel {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .report-mode-pdf .pdf-skills-grid {
          display: block !important;
          margin-top: 10px !important;
          width: 100%;
        }
        .report-mode-pdf .pdf-skill-card + .pdf-skill-card {
          margin-top: 8px !important;
        }
        .report-mode-pdf .pdf-skill-card {
          padding: 10px 16px !important;
        }
        .report-mode-pdf .pdf-skill-copy {
          margin-top: 6px !important;
          line-height: 1.35 !important;
        }
        .report-mode-pdf .pdf-skill-score {
          line-height: 1 !important;
        }
        .report-mode-pdf .pdf-skills-section {
          break-after: page;
          page-break-after: always;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .print-break, .pdf-card-break {
          break-inside: avoid;
          break-inside: avoid-page;
          page-break-inside: avoid;
        }
      ` : ""}
      @media print {
        body { background: #ffffff !important; }
        header, footer, nav, .no-print { display: none !important; }
        .report-page:not(.report-mode-pdf) { padding: 0 !important; background: #ffffff !important; color: #111827 !important; }
        .report-page.report-mode-pdf { background: #020617 !important; color: #111827 !important; }
        .report-page:not(.report-mode-pdf) .report-canvas { box-shadow: none !important; border: 0 !important; max-width: none !important; }
        .report-mode-pdf .report-canvas { box-shadow: 0 30px 100px rgba(15,23,42,0.35) !important; }
        .report-mode-pdf .pdf-skills-section { break-after: page; page-break-after: always; break-inside: avoid; page-break-inside: avoid; }
        .report-mode-pdf .pdf-skills-panel { break-inside: avoid; page-break-inside: avoid; }
        .report-mode-pdf .pdf-skills-grid { display: block !important; margin-top: 10px !important; width: 100%; }
        .report-mode-pdf .pdf-skill-card + .pdf-skill-card { margin-top: 8px !important; }
        .report-mode-pdf .pdf-skill-card { padding: 10px 16px !important; }
        .report-mode-pdf .pdf-skill-copy { margin-top: 6px !important; line-height: 1.35 !important; }
        .report-mode-pdf .pdf-skill-score { line-height: 1 !important; }
        .print-break, .pdf-card-break { break-inside: avoid; break-inside: avoid-page; page-break-inside: avoid; }
      }
    `}</style>
  );
}

export function ReportView({
  report,
  showPrintButton = true,
  onDownloadPdf,
  pdfLoading = false,
  pdfError = null,
  mode = "screen"
}: {
  report: PremiumReport;
  showPrintButton?: boolean;
  onDownloadPdf?: () => void | Promise<void>;
  pdfLoading?: boolean;
  pdfError?: string | null;
  mode?: "screen" | "pdf";
}) {
  const showExportControls = showPrintButton && mode !== "pdf";

  return (
    <div className={`report-page bg-slate-950 px-4 py-8 sm:px-6 lg:px-8 ${mode === "pdf" ? "report-mode-pdf" : ""}`}>
      <PrintStyle mode={mode} />
      <article className="report-canvas mx-auto max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-slate-950 shadow-[0_30px_100px_rgba(15,23,42,0.35)]">
        <section className="bg-gradient-to-br from-slate-950 via-indigo-950 to-cyan-950 px-6 py-8 text-white sm:px-10">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-cyan-200">TryBrainTest Premium Report</p>
              <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Αναφορά επίδοσης IQ-style Assessment</h1>
              <p className="mt-4 max-w-3xl text-base leading-7 text-slate-200">
                Ακριβής σύνοψη επίδοσης βασισμένη στο TryBrainTest cognitive challenge, με έμφαση σε μοτίβα, χωρική σκέψη, συμβολική λογική, εξαγωγή κανόνων και προσοχή.
              </p>
            </div>
            {showExportControls || mode === "pdf" ? (
              <div className={`${showExportControls ? "no-print" : "pdf-export-spacer"} flex flex-col gap-2 sm:items-end`}>
                <button
                  type="button"
                  onClick={onDownloadPdf ?? (() => window.print())}
                  disabled={pdfLoading}
                  tabIndex={showExportControls ? undefined : -1}
                  aria-hidden={showExportControls ? undefined : true}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-cyan-200/30 bg-cyan-200/10 px-4 text-sm font-bold text-cyan-50 hover:bg-cyan-200/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FileDown className="mr-2" size={18} /> {pdfLoading ? "Preparing PDF..." : "Download PDF"}
                </button>
              </div>
            ) : null}
          </div>
          <div className="mt-7 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <Meta label="Report ID" value={report.reportId} />
            <Meta label="Ημερομηνία" value={new Date(report.createdAt).toLocaleDateString("el-GR")} />
            <Meta label="Διάρκεια" value={formatDuration(report.totalTimeMs)} />
            <Meta label="Μορφή" value="30-question IQ-style Assessment" />
            <Meta label="Ηλικιακό εύρος" value={ageBandLabel(report.ageBand)} />
          </div>
        </section>

        <section className="grid gap-5 px-6 py-6 sm:px-10 lg:grid-cols-[0.88fr_1.12fr]">
          <div className="print-break rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center gap-3 text-indigo-700">
              <Gauge size={28} />
              <p className="font-bold">IQ Score</p>
            </div>
            <div className="mt-6 flex items-end gap-4">
              <p className="text-7xl font-black tracking-tight text-slate-950">{report.score.brainTestScore}</p>
              <span className="mb-3 rounded-full bg-indigo-100 px-3 py-1 text-sm font-bold text-indigo-800">{report.score.scoreBand}</span>
            </div>
            <div className="mt-4 space-y-1 text-sm leading-6 text-slate-600">
              <p>
                Εύρος εκτίμησης IQ Score: <strong>{report.score.estimateRange}</strong>
              </p>
              <p>{percentileSentence(report.score.percentileStyleLabel)}</p>
            </div>
            <p className="mt-4 rounded-xl bg-slate-100 p-4 text-sm leading-6 text-slate-600">
              Ενδεικτικός δείκτης επίδοσης στο TryBrainTest IQ-style assessment.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <MetricCard icon={<ShieldCheck size={24} />} label="Συνολική ακρίβεια" value={`${report.accuracy.accuracyPercent}%`} copy="Σταθμισμένη μαζί με τη δυσκολία.">
              <p className="mt-4 text-sm font-black text-slate-950">{report.accuracy.correctCount}/{report.totalQuestions} σωστές</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Ποσοστό σωστών απαντήσεων στο ολοκληρωμένο τεστ.</p>
            </MetricCard>
            <MetricCard icon={<Clock3 size={24} />} label="Χρονικό προφίλ" value={report.timing.timePerformanceLabel} copy={`Μ.Ο. ${formatDuration(report.averageTimeMs)} ανά ερώτηση.`} />
            <MetricCard icon={<Zap size={24} />} label="Συνέπεια" value={report.responseQuality.consistencyLabel} copy={`${report.responseQuality.rushedGuessCount} rushed guesses.`} />
          </div>
        </section>

        <section className="pdf-skills-section px-6 pb-6 sm:px-10">
          <div className="pdf-skills-panel rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-950">Ανάλυση δεξιοτήτων</h2>
                <p className="mt-2 text-sm text-slate-600">Οι 30 ερωτήσεις ομαδοποιούνται σε ευρύτερες επαγγελματικές διαστάσεις επίδοσης.</p>
              </div>
            </div>
            <div className="pdf-skills-grid mt-5 grid gap-4 lg:grid-cols-5">
              {report.dimensions.map((dimension) => (
                <div key={dimension.dimension} className="pdf-card-break pdf-skill-card rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start gap-3">
                    {iconForDimension(dimension)}
                    <p className="text-sm font-bold text-slate-900">{dimension.label}</p>
                  </div>
                  <p className="pdf-skill-score mt-4 text-3xl font-black text-slate-950">{dimension.scorePercent}%</p>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-cyan-500" style={{ width: `${clampPercent(dimension.scorePercent)}%` }} />
                  </div>
                  <p className="pdf-skill-copy mt-3 text-xs leading-5 text-slate-600">{naturalDimensionInterpretation(dimension.interpretation)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-5 px-6 pb-6 sm:px-10 lg:grid-cols-2">
          <Panel title="Χρονική ανάλυση">
            <InfoRow label="Συνολικός χρόνος" value={formatDuration(report.timing.totalTimeMs)} />
            <InfoRow label="Μέσος χρόνος ανά ερώτηση" value={formatDuration(report.timing.averageTimeMs)} />
            <InfoRow label="Ταχύτερη σωστή απάντηση" value={report.timing.fastestCorrectAnswer ? formatDuration(report.timing.fastestCorrectAnswer.responseTimeMs) : "—"} />
            <InfoRow label="Αργότερη σωστή απάντηση" value={report.timing.slowestCorrectAnswer ? formatDuration(report.timing.slowestCorrectAnswer.responseTimeMs) : "—"} />
          </Panel>
          <Panel title="Ποιότητα απαντήσεων">
            <InfoRow label="Δείκτης συνέπειας" value={consistencyIndexLabel(report.responseQuality.consistencyLabel)} />
            <InfoRow label="Πολύ γρήγορες λάθος απαντήσεις" value={String(report.responseQuality.rushedGuessCount)} />
            <InfoRow label="Δυσκολία που έφτασες" value={report.difficulty.hardestDifficultyReached} />
            <InfoRow label="Ολοκλήρωση" value={`${report.completedQuestions}/${report.totalQuestions}`} />
          </Panel>
        </section>

        <section className="grid gap-5 px-6 pb-8 sm:px-10 lg:grid-cols-[1.05fr_0.95fr]">
          <Panel title="Ερμηνεία">
            <div className="grid gap-3">
              {interpretationCards(report).map((section) => (
                <div key={section.title} className="pdf-card-break rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-black text-slate-950">{section.title}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{section.body}</p>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Προτάσεις επόμενων βημάτων">
            <div className="grid gap-3">
              {report.recommendations.map((recommendation) => (
                <div key={recommendation.title} className="pdf-card-break rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                  {recommendation.focusLabel ? (
                    <span className="mb-2 inline-flex rounded-full bg-indigo-100 px-2 py-1 text-xs font-bold text-indigo-700">
                      {recommendation.focusLabel}
                    </span>
                  ) : null}
                  <p className="font-bold text-slate-950">{recommendation.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{recommendation.body}</p>
                </div>
              ))}
            </div>
          </Panel>
        </section>

        <section className="grid gap-5 border-t border-slate-200 bg-white px-6 py-6 sm:px-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-2xl bg-gradient-to-br from-slate-950 to-indigo-950 p-6 text-center text-white">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-200">{report.score.badgeLabel}</p>
            <p className="mt-4 text-6xl font-black">{report.score.brainTestScore}</p>
            <p className="mt-3 text-sm text-slate-300">{performanceLabel(report)}</p>
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-950">Μεθοδολογία</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">{report.methodologyNote}</p>
            {showExportControls ? (
              <>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <Button type="button" onClick={onDownloadPdf ?? (() => window.print())} disabled={pdfLoading} className="no-print bg-slate-950 text-white hover:bg-slate-800">
                    <FileDown className="mr-2" size={18} /> {onDownloadPdf ? (pdfLoading ? "Preparing PDF..." : "Download PDF") : "Λήψη PDF"}
                  </Button>
                </div>
                {pdfError ? <p className="no-print mt-3 text-sm font-semibold text-rose-600">{pdfError}</p> : null}
              </>
            ) : null}
          </div>
        </section>
      </article>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/10 p-3">
      <p className="text-xs text-slate-300">{label}</p>
      <p className="mt-1 font-bold text-white">{value}</p>
    </div>
  );
}

function MetricCard({ icon, label, value, copy, children }: { icon: ReactNode; label: string; value: string; copy: string; children?: ReactNode }) {
  return (
    <div className="print-break rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="text-indigo-700">{icon}</div>
      <p className="mt-4 text-sm font-semibold text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-black text-slate-950">{value}</p>
      <p className="mt-2 text-sm leading-6 text-slate-600">{copy}</p>
      {children}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="print-break rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="text-2xl font-black text-slate-950">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3 text-sm last:border-b-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-slate-950">{value}</span>
    </div>
  );
}
